// src/views/TrainerView.js
import React, { useState, useEffect, useRef } from "react";
import { FiEdit2, FiTrash2, FiPlus, FiCheck, FiX, FiExternalLink, FiUpload, FiChevronDown } from "react-icons/fi";
import { db } from "../firebase";
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";

// ── Lege forms ──────────────────────────────────────────────
function emptySessionForm() {
  return {
    date: "",
    time: "09:00",
    typeId: "",
    trainerEmail: "",
    audienceIds: [],
    allowedEmailsRaw: "",
  };
}
function emptyCatalogForm() {
  return { name: "", duration: "", description: "" };
}

function emptyTrainerForm() {
  return { name: "", email: "", role: "trainer" };
}
function emptyAudienceForm() {
  return { label: "", domains: "" };
}

// Pseudo-doelgroep: losse medewerkers op e-mailadres
const ANDERS = "anders";

export default function TrainerView({ sessions, role, onAdd, onUpdate, onDelete }) {
  const isAdmin = role === "admin";
  const [activeTab, setActiveTab] = useState("sessions");
  const csvInputRef = useRef(null);

  const handleCsvUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const emails = text
        .split(/[\n,;]+/)
        .map((s) => s.trim().toLowerCase())
        .filter((s) => s.includes("@"));
      setSessionForm((prev) => ({
        ...prev,
        allowedEmailsRaw: [...new Set([
          ...prev.allowedEmailsRaw.split(",").map((s) => s.trim()).filter(Boolean),
          ...emails,
        ])].join(", "),
      }));
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const [trainingTypes, setTrainingTypes] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [users, setUsers] = useState([]);

  // Session form state
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [sessionForm, setSessionForm] = useState(emptySessionForm());
  const [audienceMenuOpen, setAudienceMenuOpen] = useState(false);
  const audienceMenuRef = useRef(null);

  // Catalog form state
  const [catalogForm, setCatalogForm] = useState(emptyCatalogForm());
  const [editingCatalogId, setEditingCatalogId] = useState(null);
  const [catalogOpen, setCatalogOpen] = useState(false);

  // Trainer form state
  const [trainerForm, setTrainerForm] = useState(emptyTrainerForm());
  const [editingTrainerId, setEditingTrainerId] = useState(null);
  const [trainerOpen, setTrainerOpen] = useState(false);

  // Audience form state
  const [audienceForm, setAudienceForm] = useState(emptyAudienceForm());
  const [editingAudienceId, setEditingAudienceId] = useState(null);
  const [audienceOpen, setAudienceOpen] = useState(false);

  // Notification state
  const [notification, setNotification] = useState(null);
  const notifyTimer = useRef(null);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState(null);

  // Attendee counts per session
  const [attendeeCounts, setAttendeeCounts] = useState({});

  // Sessie filters
  const [filterTrainer, setFilterTrainer] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterAudience, setFilterAudience] = useState("");
  const [filterMonth, setFilterMonth] = useState("");

  const openConfirm = (message, onConfirm) => setConfirmDialog({ message, onConfirm });
  const closeConfirm = () => setConfirmDialog(null);

  const notify = (message, type = "error") => {
    setNotification({ message, type });
    clearTimeout(notifyTimer.current);
    notifyTimer.current = setTimeout(() => setNotification(null), 3500);
  };

  useEffect(() => () => clearTimeout(notifyTimer.current), []);

  // Doelgroepen-menu sluiten bij klik buiten het veld
  useEffect(() => {
    if (!audienceMenuOpen) return;
    const onMouseDown = (e) => {
      if (audienceMenuRef.current && !audienceMenuRef.current.contains(e.target)) {
        setAudienceMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [audienceMenuOpen]);

  // ── Firestore listeners ──────────────────────────────────
  useEffect(() => {
    const u1 = onSnapshot(collection(db, "trainingTypes"), (s) =>
      setTrainingTypes(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
    );
    const u2 = onSnapshot(collection(db, "trainers"), (s) =>
      setTrainers(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
    );
    const u3 = onSnapshot(collection(db, "audiences"), (s) =>
      setAudiences(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
    );
    const u4 = onSnapshot(collection(db, "users"), (s) =>
      setUsers(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
    );
    return () => { u1(); u2(); u3(); u4(); };
  }, []);

  // Attendee counts: één listener per sessie
  useEffect(() => {
    if (sessions.length === 0) return;
    const unsubs = sessions.map((s) =>
      onSnapshot(collection(db, "sessions", s.id, "attendees"), (snap) =>
        setAttendeeCounts((prev) => ({ ...prev, [s.id]: snap.size })),
      ),
    );
    return () => unsubs.forEach((u) => u());
  }, [sessions]);

  // ── Doelgroep helpers ────────────────────────────────────
  // Labels van een sessie; valt terug op oude sessies met één audienceLabel
  const sessionAudienceLabels = (s) => {
    if (Array.isArray(s.audienceLabels) && s.audienceLabels.length > 0) return s.audienceLabels;
    if (!s.audienceLabel) return [];
    return String(s.audienceLabel).split(",").map((l) => l.trim()).filter(Boolean);
  };

  // Geselecteerde doelgroepen herleiden uit een bestaande sessie
  const sessionAudienceIds = (s) => {
    if (Array.isArray(s.audienceIds) && s.audienceIds.length > 0) {
      return s.audienceIds.filter((id) => id === ANDERS || audiences.some((a) => a.id === id));
    }
    const ids = sessionAudienceLabels(s)
      .map((l) => audiences.find((a) => a.label === l)?.id)
      .filter(Boolean);
    if (ids.length > 0) return ids;
    return s.allowedEmails?.length > 0 ? [ANDERS] : [];
  };

  const selectedAudienceIds = sessionForm.audienceIds || [];
  const individueelSelected = selectedAudienceIds.includes(ANDERS);
  const selectedAudienceLabels = selectedAudienceIds
    .map((id) => (id === ANDERS ? "Individueel" : audiences.find((a) => a.id === id)?.label))
    .filter(Boolean);

  // "Individueel" en echte doelgroepen sluiten elkaar uit
  const toggleAudience = (id) => {
    setSessionForm((prev) => {
      const cur = prev.audienceIds || [];
      if (id === ANDERS) {
        const wasOn = cur.includes(ANDERS);
        return { ...prev, audienceIds: wasOn ? [] : [ANDERS], allowedEmailsRaw: "" };
      }
      const next = cur.includes(id)
        ? cur.filter((x) => x !== id)
        : [...cur.filter((x) => x !== ANDERS), id];
      return { ...prev, audienceIds: next, allowedEmailsRaw: "" };
    });
  };

  // ── Session handlers ─────────────────────────────────────
  const handleSessionSubmit = async () => {
    const { date, time, typeId, trainerEmail } = sessionForm;
    const audienceIds = sessionForm.audienceIds || [];
    if (!date || !time || !typeId || !trainerEmail || audienceIds.length === 0) {
      notify("Vul alle velden in.");
      return;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (new Date(date) < today) {
      notify("Je kunt niet in het verleden plannen.");
      return;
    }

    const type = trainingTypes.find((t) => t.id === typeId);
    const trainer = trainers.find((t) => t.email === trainerEmail);
    const individueel = audienceIds.includes(ANDERS);
    const selected = individueel
      ? []
      : audienceIds.map((id) => audiences.find((a) => a.id === id)).filter(Boolean);

    if (!type || !trainer || (!individueel && selected.length !== audienceIds.length)) {
      notify("Training, trainer of doelgroep niet meer gevonden. Herlaad de pagina.");
      return;
    }

    const allowedEmails = (sessionForm.allowedEmailsRaw || "")
      .split(",")
      .map((e) => e.trim().toLowerCase())
      .filter(Boolean);

    if (individueel && allowedEmails.length === 0) {
      notify("Voer minimaal één e-mailadres in.");
      return;
    }

    const audienceLabels = individueel ? ["Specifiek"] : selected.map((a) => a.label);

    const data = {
      date,
      time,
      trainingName: type.name,
      duration: type.duration,
      description: type.description || "",
      trainerName: trainer.name,
      trainerEmail: trainer.email,
      audienceIds,
      audienceLabels,
      // Samengevoegd label: blijft leesbaar voor bestaande weergave/oude sessies
      audienceLabel: audienceLabels.join(", "),
      allowedDomains: individueel
        ? []
        : [...new Set(selected.flatMap((a) => a.domains || []))],
      allowedEmails,
    };

    try {
      if (editingSessionId) {
        await onUpdate(editingSessionId, data);
        notify("Sessie opgeslagen.", "success");
      } else {
        await onAdd(data);
        notify("Sessie aangemaakt.", "success");
      }
      setEditingSessionId(null);
      setSessionForm(emptySessionForm());
      setAudienceMenuOpen(false);
    } catch (err) {
      notify("Opslaan mislukt. Controleer je rechten en probeer opnieuw.");
      console.error(err);
    }
  };

  const handleSessionEdit = (s) => {
    const type = trainingTypes.find((t) => t.name === s.trainingName);
    setEditingSessionId(s.id);
    setAudienceMenuOpen(false);
    setSessionForm({
      date: s.date,
      time: s.time,
      typeId: type?.id || "",
      trainerEmail: s.trainerEmail,
      audienceIds: sessionAudienceIds(s),
      allowedEmailsRaw: (s.allowedEmails || []).join(", "),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSessionCancel = () => {
    setEditingSessionId(null);
    setSessionForm(emptySessionForm());
    setAudienceMenuOpen(false);
  };

  // ── Catalog handlers ─────────────────────────────────────
  const openNewCatalog = () => {
    setEditingCatalogId(null);
    setCatalogForm(emptyCatalogForm());
    setCatalogOpen(true);
  };

  const openEditCatalog = (t) => {
    setEditingCatalogId(t.id);
    setCatalogForm({
      name: t.name,
      duration: String(t.duration),
      description: t.description || "",
    });
    setCatalogOpen(true);
  };

  const closeCatalog = () => {
    setCatalogOpen(false);
    setEditingCatalogId(null);
    setCatalogForm(emptyCatalogForm());
  };

  const handleCatalogSubmit = async () => {
    const { name, duration, description } = catalogForm;
    if (!name || !duration) {
      notify("Naam en duur zijn verplicht.");
      return;
    }
    const data = {
      name: name.trim(),
      duration: Number(duration),
      description: description.trim(),
    };

    if (editingCatalogId) {
      await updateDoc(doc(db, "trainingTypes", editingCatalogId), data);
    } else {
      await addDoc(collection(db, "trainingTypes"), data);
    }
    closeCatalog();
  };

  const handleCatalogDelete = async (id) => {
    openConfirm("Training uit de catalogus verwijderen?", () =>
      deleteDoc(doc(db, "trainingTypes", id)),
    );
  };

  // ── Trainer handlers (admin only) ────────────────────────
  const openNewTrainer = () => {
    setEditingTrainerId(null);
    setTrainerForm(emptyTrainerForm());
    setTrainerOpen(true);
  };

  const openEditTrainer = (t) => {
    const matchedUser = users.find((u) => u.email?.toLowerCase() === t.email?.toLowerCase());
    setEditingTrainerId(t.id);
    setTrainerForm({ name: t.name, email: t.email, role: matchedUser?.role || "trainer" });
    setTrainerOpen(true);
  };

  const closeTrainer = () => {
    setTrainerOpen(false);
    setEditingTrainerId(null);
    setTrainerForm(emptyTrainerForm());
  };

  const handleTrainerSubmit = async () => {
    const { name, email, role } = trainerForm;
    if (!name || !email) {
      notify("Naam en e-mail zijn verplicht.");
      return;
    }
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail.endsWith("@adapta.nl")) {
      notify("Alleen @adapta.nl e-mailadressen zijn toegestaan.");
      return;
    }
    const data = { name: name.trim(), email: trimmedEmail };
    if (editingTrainerId) {
      await updateDoc(doc(db, "trainers", editingTrainerId), data);
    } else {
      await addDoc(collection(db, "trainers"), data);
    }
    await handleRoleChange(trimmedEmail, role);
    closeTrainer();
  };

  const handleTrainerDelete = async (id) => {
    openConfirm("Trainer verwijderen?", () =>
      deleteDoc(doc(db, "trainers", id)),
    );
  };

  // ── Audience handlers ────────────────────────────────────
  const openNewAudience = () => {
    setEditingAudienceId(null);
    setAudienceForm(emptyAudienceForm());
    setAudienceOpen(true);
  };

  const openEditAudience = (a) => {
    setEditingAudienceId(a.id);
    setAudienceForm({ label: a.label, domains: (a.domains || []).join(", ") });
    setAudienceOpen(true);
  };

  const closeAudience = () => {
    setAudienceOpen(false);
    setEditingAudienceId(null);
    setAudienceForm(emptyAudienceForm());
  };

  const handleAudienceSubmit = async () => {
    const { label, domains } = audienceForm;
    if (!label) {
      notify("Naam is verplicht.");
      return;
    }
    const data = {
      label: label.trim(),
      domains: domains
        .split(",")
        .map((d) => d.trim().toLowerCase())
        .filter(Boolean),
    };
    if (editingAudienceId) {
      await updateDoc(doc(db, "audiences", editingAudienceId), data);
    } else {
      await addDoc(collection(db, "audiences"), data);
    }
    closeAudience();
  };

  const handleAudienceDelete = async (id) => {
    openConfirm("Doelgroep verwijderen?", () =>
      deleteDoc(doc(db, "audiences", id)),
    );
  };

  // ── User role handler (admin only) ───────────────────────
  const handleRoleChange = async (userEmail, newRole) => {
    const matched = users.find(
      (u) => u.email?.toLowerCase() === userEmail.toLowerCase(),
    );
    if (matched) {
      await updateDoc(doc(db, "users", matched.id), { role: newRole });
    }
  };

  // ── Helpers ──────────────────────────────────────────────
  const now = new Date();
  const sortedSessions = [...sessions]
    .filter((s) => {
      if (s.date && s.time) {
        const [h, m] = s.time.split(":").map(Number);
        const end = new Date(s.date);
        end.setHours(h, m + Number(s.duration || 0), 0, 0);
        if (end < now) return false;
      }
      if (filterTrainer && s.trainerEmail !== filterTrainer) return false;
      if (filterType) {
        const t = trainingTypes.find((tt) => tt.id === filterType);
        if (!t || s.trainingName !== t.name) return false;
      }
      if (filterAudience) {
        if (filterAudience === "anders") {
          if (!(s.allowedEmails?.length > 0)) return false;
        } else {
          const a = audiences.find((aa) => aa.id === filterAudience);
          if (!a) return false;
          if (Array.isArray(s.audienceIds) && s.audienceIds.length > 0) {
            if (!s.audienceIds.includes(a.id)) return false;
          } else if (!sessionAudienceLabels(s).includes(a.label)) return false;
        }
      }
      if (filterMonth && s.date?.slice(0, 7) !== filterMonth) return false;
      return true;
    })
    .sort(
      (a, b) => new Date(`${a.date}T${a.time}`) - new Date(`${b.date}T${b.time}`),
    );

  const statusLabel = (s) =>
    ({
      new: { label: "Nieuw", cls: "status-new" },
      creating: { label: "Aanmaken…", cls: "status-creating" },
      ready: { label: "Gepland", cls: "status-ready" },
      error: { label: "Fout", cls: "status-error" },
      retrying: { label: "Opnieuw…", cls: "status-creating" },
    })[s.status] || { label: s.status, cls: "" };

  // ── Render ───────────────────────────────────────────────
  return (
    <div className="trainer-view">
      {notification && (
        <div className={`notify-bar notify-${notification.type}`}>
          {notification.message}
        </div>
      )}

      {confirmDialog && (
        <div className="modal-overlay" onClick={closeConfirm}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <p className="modal-message">{confirmDialog.message}</p>
            <div className="modal-actions">
              <button className="btn-ghost" onClick={closeConfirm}>
                Annuleren
              </button>
              <button
                className="btn-danger"
                onClick={() => {
                  confirmDialog.onConfirm();
                  closeConfirm();
                }}
              >
                Verwijderen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs">
        {["sessions", "catalog", "trainers", "audiences"].map((t) => (
          <button
            key={t}
            className={`tab ${activeTab === t ? "tab-active" : ""}`}
            onClick={() => setActiveTab(t)}
          >
            {{ sessions: "Sessies", catalog: "Catalogus", trainers: "Trainers", audiences: "Doelgroepen" }[t]}
          </button>
        ))}
        {activeTab === "sessions" && (
          <button className="tab-add" disabled title="Formulier staat hieronder">
            <FiPlus size={16} />
          </button>
        )}
        {activeTab === "catalog" && (
          <button className="tab-add" onClick={openNewCatalog} title="Nieuwe training">
            <FiPlus size={16} />
          </button>
        )}
        {activeTab === "trainers" && isAdmin && (
          <button className="tab-add" onClick={openNewTrainer} title="Nieuwe trainer">
            <FiPlus size={16} />
          </button>
        )}
        {activeTab === "audiences" && (
          <button className="tab-add" onClick={openNewAudience} title="Nieuwe doelgroep">
            <FiPlus size={16} />
          </button>
        )}
      </div>

      {/* ══ SESSIONS TAB ══ */}
      {activeTab === "sessions" && (
        <>
          <div className="card form-card">
            <h2 className="card-title">
              {editingSessionId ? "Sessie bewerken" : "Nieuwe sessie plannen"}
            </h2>

            {/* Rij 1: Trainer, Training, Doelgroep */}
            <div className="form-grid form-grid-3">
              <div className="form-field">
                <label>Trainer</label>
                <select
                  value={sessionForm.trainerEmail}
                  onChange={(e) => setSessionForm({ ...sessionForm, trainerEmail: e.target.value })}
                >
                  <option value="">Kies trainer</option>
                  {trainers.map((t) => (
                    <option key={t.id} value={t.email}>{t.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-field">
                <label>Training</label>
                <select
                  value={sessionForm.typeId}
                  onChange={(e) => setSessionForm({ ...sessionForm, typeId: e.target.value })}
                >
                  <option value="">Kies training</option>
                  {trainingTypes.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-field">
                <label>Doelgroepen</label>
                <div className="multiselect" ref={audienceMenuRef}>
                  <button
                    type="button"
                    className="multiselect-trigger"
                    onClick={() => setAudienceMenuOpen((o) => !o)}
                  >
                    <span className={selectedAudienceLabels.length === 0 ? "multiselect-placeholder" : ""}>
                      {selectedAudienceLabels.length === 0
                        ? "Kies doelgroepen"
                        : selectedAudienceLabels.length <= 2
                          ? selectedAudienceLabels.join(", ")
                          : `${selectedAudienceLabels.length} doelgroepen`}
                    </span>
                    <FiChevronDown size={14} />
                  </button>
                  {audienceMenuOpen && (
                    <div className="multiselect-panel">
                      {audiences.length === 0 && (
                        <p className="multiselect-empty">Nog geen doelgroepen aangemaakt.</p>
                      )}
                      {audiences.map((a) => (
                        <label key={a.id} className="multiselect-option">
                          <input
                            type="checkbox"
                            checked={selectedAudienceIds.includes(a.id)}
                            onChange={() => toggleAudience(a.id)}
                          />
                          <span>{a.label}</span>
                        </label>
                      ))}
                      <label className="multiselect-option multiselect-option-sep">
                        <input
                          type="checkbox"
                          checked={individueelSelected}
                          onChange={() => toggleAudience(ANDERS)}
                        />
                        <span>Individueel</span>
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Rij 2: conditioneel e-mailveld */}
            {individueelSelected && (
              <div style={{ marginBottom: 10 }}>
                <div className="form-field">
                  <label>Medewerkers</label>
                  <div className="input-with-icon">
                    <input
                      type="text"
                      placeholder="jan@bedrijf.nl, lisa@bedrijf.nl"
                      value={sessionForm.allowedEmailsRaw}
                      onChange={(e) => setSessionForm({ ...sessionForm, allowedEmailsRaw: e.target.value })}
                    />
                    <button
                      type="button"
                      className="input-icon-btn"
                      onClick={() => csvInputRef.current.click()}
                      title="CSV uploaden"
                    >
                      <FiUpload size={14} />
                    </button>
                    <input
                      ref={csvInputRef}
                      type="file"
                      accept=".csv,.txt"
                      style={{ display: "none" }}
                      onChange={handleCsvUpload}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Rij 3: Datum, Tijd, Aanmaken */}
            <div className="form-grid form-grid-3">
              <div className="form-field">
                <label>Datum</label>
                <input
                  type="date"
                  value={sessionForm.date}
                  min={new Date().toISOString().split("T")[0]}
                  onChange={(e) => setSessionForm({ ...sessionForm, date: e.target.value })}
                />
              </div>

              <div className="form-field">
                <label>Tijd</label>
                <input
                  type="time"
                  value={sessionForm.time}
                  onChange={(e) => setSessionForm({ ...sessionForm, time: e.target.value })}
                />
              </div>

              <div className="form-field form-field-action">
                {editingSessionId ? (
                  <div className="session-submit-row">
                    <button className="btn-ghost btn-icon-only" onClick={handleSessionCancel} title="Annuleren">
                      <FiX size={16} />
                    </button>
                    <button className="btn-primary btn-icon-only btn-session-submit" onClick={handleSessionSubmit} title="Opslaan">
                      <FiCheck size={16} />
                    </button>
                  </div>
                ) : (
                  <button className="btn-primary btn-session-submit" onClick={handleSessionSubmit}>
                    Aanmaken
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="filter-row">
            <select
              className="filter-select"
              value={filterTrainer}
              onChange={(e) => setFilterTrainer(e.target.value)}
            >
              <option value="">Alle trainers</option>
              {trainers.map((t) => (
                <option key={t.id} value={t.email}>{t.name}</option>
              ))}
            </select>
            <select
              className="filter-select"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="">Alle trainingen</option>
              {trainingTypes.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
            <select
              className="filter-select"
              value={filterAudience}
              onChange={(e) => setFilterAudience(e.target.value)}
            >
              <option value="">Alle doelgroepen</option>
              {audiences.map((a) => (
                <option key={a.id} value={a.id}>{a.label}</option>
              ))}
              <option value="anders">Individueel</option>
            </select>
            <select
              className="filter-select"
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
            >
              <option value="">Alle maanden</option>
              {[...new Set(sessions.map((s) => s.date?.slice(0, 7)).filter(Boolean))]
                .sort()
                .map((ym) => {
                  const [y, m] = ym.split("-");
                  const label = new Date(y, m - 1).toLocaleDateString("nl-NL", { month: "long", year: "numeric" });
                  return <option key={ym} value={ym}>{label.charAt(0).toUpperCase() + label.slice(1)}</option>;
                })}
            </select>
          </div>

          <div className="sessions-list">
            {sortedSessions.map((s) => {
              const st = statusLabel(s);
              return (
                <div
                  key={s.id}
                  className={`card session-card ${editingSessionId === s.id ? "session-card-editing" : ""}`}
                >
                  <div className="session-card-top">
                    <div>
                      <h3 className="session-name">{s.trainingName}</h3>
                      <p className="session-audience">{s.audienceLabel}</p>
                      <p className="session-meta">
                        {formatDate(s.date)} · {formatTime(s.time, s.duration)} · {s.trainerName}
                      </p>
                    </div>
                    <span className={`status-pill ${st.cls}`}>{st.label}</span>
                  </div>
                  <div className="session-card-bottom">
                    <div className="session-details">
                      <span className="attendee-count">
                        {attendeeCounts[s.id] ?? 0} ingeschreven
                      </span>
                    </div>
                    <div className="session-actions">
                      {s.meetLink && (
                        <a
                          href={s.meetLink}
                          target="_blank"
                          rel="noreferrer"
                          className="btn-ghost-sm btn-no-underline"
                          title="Deelnemen"
                        >
                          <span className="btn-label">Deelnemen</span>
                          <FiExternalLink className="btn-icon" size={15} />
                        </a>
                      )}
                      <button
                        className="btn-ghost-sm"
                        onClick={() => handleSessionEdit(s)}
                        title="Bewerken"
                      >
                        <span className="btn-label">Bewerken</span>
                        <FiEdit2 className="btn-icon" size={15} />
                      </button>
                      <button
                        className="btn-danger-sm"
                        onClick={() =>
                          openConfirm("Sessie verwijderen?", () => onDelete(s))
                        }
                        title="Verwijderen"
                      >
                        <span className="btn-label">Verwijderen</span>
                        <FiTrash2 className="btn-icon" size={15} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ══ CATALOG TAB ══ */}
      {activeTab === "catalog" && (
        <>
          {/* Inline form — toont als catalogOpen === true */}
          {catalogOpen && (
            <div className="card form-card">
              <h2 className="card-title">
                {editingCatalogId ? "Training aanpassen" : "Nieuwe training"}
              </h2>

              <div className="form-grid catalog-form-grid">
                <div className="form-field form-field-wide">
                  <label>Naam</label>
                  <input
                    type="text"
                    placeholder="bijv. Veiligheid op de werkvloer"
                    value={catalogForm.name}
                    onChange={(e) =>
                      setCatalogForm({ ...catalogForm, name: e.target.value })
                    }
                  />
                </div>

                <div className="form-field">
                  <label>Duur (minuten)</label>
                  <input
                    type="number"
                    min="15"
                    step="15"
                    placeholder="60"
                    value={catalogForm.duration}
                    onChange={(e) =>
                      setCatalogForm({
                        ...catalogForm,
                        duration: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="form-field form-field-full">
                  <label>Beschrijving</label>
                  <textarea
                    rows={3}
                    placeholder="Korte omschrijving van de training…"
                    value={catalogForm.description}
                    onChange={(e) =>
                      setCatalogForm({
                        ...catalogForm,
                        description: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div className="form-actions">
                <button className="btn-ghost" onClick={closeCatalog}>
                  Annuleren
                </button>
                <button className="btn-primary" onClick={handleCatalogSubmit}>
                  {editingCatalogId ? "Opslaan" : "Toevoegen"}
                </button>
              </div>
            </div>
          )}


          {/* Catalog list */}
          <div className="catalog-list">
            {trainingTypes.length === 0 && !catalogOpen && (
              <div className="empty-state-box">
                <span>📚</span>
                <p>Nog geen trainingen in de catalogus.</p>
              </div>
            )}

            {trainingTypes.map((t) => (
              <div
                key={t.id}
                className={`card catalog-card ${editingCatalogId === t.id ? "catalog-card-editing" : ""}`}
              >
                <div className="catalog-card-body">
                  <div className="catalog-card-info">
                    <h3 className="catalog-name">{t.name}</h3>
                    <span className="catalog-duration">{t.duration} min</span>
                    {t.description && (
                      <p className="catalog-desc">{t.description}</p>
                    )}
                  </div>
                  <div className="catalog-card-actions">
                    <button
                      className="btn-ghost-sm"
                      onClick={() => openEditCatalog(t)}
                      title="Bewerken"
                    >
                      <span className="btn-label">Bewerken</span>
                      <FiEdit2 className="btn-icon" size={15} />
                    </button>
                    <button
                      className="btn-danger-sm"
                      onClick={() => handleCatalogDelete(t.id)}
                      title="Verwijderen"
                    >
                      <span className="btn-label">Verwijderen</span>
                      <FiTrash2 className="btn-icon" size={15} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ══ TRAINERS TAB ══ */}
      {activeTab === "trainers" && (
        <>
          {isAdmin && trainerOpen && (
            <div className="card form-card">
              <h2 className="card-title">
                {editingTrainerId ? "Trainer bewerken" : "Nieuwe trainer"}
              </h2>
              <div className="form-grid">
                <div className="form-field">
                  <label>Naam</label>
                  <input
                    type="text"
                    placeholder="bijv. Jan de Vries"
                    value={trainerForm.name}
                    onChange={(e) =>
                      setTrainerForm({ ...trainerForm, name: e.target.value })
                    }
                  />
                </div>
                <div className="form-field">
                  <label>E-mail (@adapta.nl)</label>
                  <input
                    type="email"
                    placeholder="jan@adapta.nl"
                    value={trainerForm.email}
                    onChange={(e) =>
                      setTrainerForm({ ...trainerForm, email: e.target.value })
                    }
                  />
                </div>
                <div className="form-field">
                  <label>Rol</label>
                  <select
                    value={trainerForm.role}
                    onChange={(e) =>
                      setTrainerForm({ ...trainerForm, role: e.target.value })
                    }
                  >
                    <option value="employee">Employee</option>
                    <option value="trainer">Trainer</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>
              <div className="form-actions">
                <button className="btn-ghost" onClick={closeTrainer}>
                  Annuleren
                </button>
                <button className="btn-primary" onClick={handleTrainerSubmit}>
                  {editingTrainerId ? "Opslaan" : "Toevoegen"}
                </button>
              </div>
            </div>
          )}


          <div className="trainers-list">
            {trainers.length === 0 && !trainerOpen && (
              <p className="empty-state">Geen trainers gevonden.</p>
            )}
            {trainers.map((t) => {
              return (
                <div
                  key={t.id}
                  className={`card trainer-card ${editingTrainerId === t.id ? "catalog-card-editing" : ""}`}
                >
                  <h3 className="trainer-card-name">{t.name}</h3>
                  {isAdmin && (
                    <div className="trainer-card-actions">
                      <button
                        className="btn-ghost-sm"
                        onClick={() => openEditTrainer(t)}
                        title="Bewerken"
                      >
                        <span className="btn-label">Bewerken</span>
                        <FiEdit2 className="btn-icon" size={15} />
                      </button>
                      <button
                        className="btn-danger-sm"
                        onClick={() => handleTrainerDelete(t.id)}
                        title="Verwijderen"
                      >
                        <span className="btn-label">Verwijderen</span>
                        <FiTrash2 className="btn-icon" size={15} />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ══ AUDIENCES TAB ══ */}
      {activeTab === "audiences" && (
        <>
          {audienceOpen && (
            <div className="card form-card">
              <h2 className="card-title">
                {editingAudienceId ? "Doelgroep bewerken" : "Nieuwe doelgroep"}
              </h2>
              <div className="form-grid">
                <div className="form-field form-field-wide">
                  <label>Naam</label>
                  <input
                    type="text"
                    placeholder="bijv. Medewerkers Adapta"
                    value={audienceForm.label}
                    onChange={(e) =>
                      setAudienceForm({ ...audienceForm, label: e.target.value })
                    }
                  />
                </div>
                <div className="form-field form-field-full">
                  <label>Domeinen (komma-gescheiden, leeg = iedereen)</label>
                  <input
                    type="text"
                    placeholder="bijv. adapta.nl, partner.nl"
                    value={audienceForm.domains}
                    onChange={(e) =>
                      setAudienceForm({ ...audienceForm, domains: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className="form-actions">
                <button className="btn-ghost" onClick={closeAudience}>
                  Annuleren
                </button>
                <button className="btn-primary" onClick={handleAudienceSubmit}>
                  {editingAudienceId ? "Opslaan" : "Toevoegen"}
                </button>
              </div>
            </div>
          )}


          <div className="catalog-list">
            {audiences.length === 0 && !audienceOpen && (
              <div className="empty-state-box">
                <span>🎯</span>
                <p>Nog geen doelgroepen aangemaakt.</p>
              </div>
            )}
            {audiences.map((a) => (
              <div
                key={a.id}
                className={`card catalog-card ${editingAudienceId === a.id ? "catalog-card-editing" : ""}`}
              >
                <div className="catalog-card-body">
                  <div className="catalog-card-info">
                    <h3 className="catalog-name">{a.label}</h3>
                    <p className="catalog-desc">
                      {(a.domains || []).length > 0
                        ? `Domeinen: ${(a.domains || []).map((d) => d.replace(/^@/, "")).join(", ")}`
                        : "Iedereen"}
                    </p>
                  </div>
                  <div className="catalog-card-actions">
                    <button
                      className="btn-ghost-sm"
                      onClick={() => openEditAudience(a)}
                      title="Bewerken"
                    >
                      <span className="btn-label">Bewerken</span>
                      <FiEdit2 className="btn-icon" size={15} />
                    </button>
                    <button
                      className="btn-danger-sm"
                      onClick={() => handleAudienceDelete(a.id)}
                      title="Verwijderen"
                    >
                      <span className="btn-label">Verwijderen</span>
                      <FiTrash2 className="btn-icon" size={15} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-");
  const formattedDate = new Date(y, m - 1, d).toLocaleDateString("nl-NL", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
}

function formatTime(time, duration) {
  if (!time) return "";
  const [h, m] = time.split(":").map(Number);
  const endMinutes = h * 60 + m + Number(duration || 0);
  const eh = Math.floor(endMinutes / 60) % 24;
  const em = endMinutes % 60;
  const end = `${String(eh).padStart(2, "0")}:${String(em).padStart(2, "0")}`;
  return `${time} – ${end}`;
}
